'use client'

import React, { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { BookOpen, ExternalLink } from 'lucide-react'

interface PassageData {
  reference: string
  text: string
  translation: string
  gatewayUrl: string
}

const clientCache = new Map<string, PassageData>()

async function loadPassage(ref: string): Promise<PassageData> {
  const key = ref.trim().toLowerCase()
  const existing = clientCache.get(key)
  if (existing) return existing

  const res = await fetch(`/api/wlib-bible?ref=${encodeURIComponent(ref)}`)
  const data = await res.json()
  const entry: PassageData = {
    reference: data.reference || ref,
    text: data.text || '',
    translation: data.translation || 'World English Bible',
    gatewayUrl:
      data.gatewayUrl ||
      `https://www.biblegateway.com/passage/?search=${encodeURIComponent(ref)}&version=ESV`,
  }
  clientCache.set(key, entry)
  return entry
}

export function BibleVerseEnhancer() {
  const pathname = usePathname()
  const [activeRef, setActiveRef] = useState<string | null>(null)
  const [passage, setPassage] = useState<PassageData | null>(null)
  const [loading, setLoading] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number; placement: 'above' | 'below' }>({
    top: 0,
    left: 0,
    placement: 'above',
  })

  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearHideTimer = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }

  const scheduleHide = () => {
    clearHideTimer()
    hideTimeoutRef.current = setTimeout(() => {
      setActiveRef(null)
    }, 220)
  }

  // Hydrate inline {bible ...} readings on route change
  useEffect(() => {
    setActiveRef(null)

    const inlineNodes = document.querySelectorAll<HTMLElement>(
      '.wl-bible-inline[data-bible-inline]:not([data-hydrated="true"])',
    )
    inlineNodes.forEach((node) => {
      const ref = node.getAttribute('data-bible-inline')
      if (!ref) return
      node.setAttribute('data-hydrated', 'true')

      loadPassage(ref)
        .then((d) => {
          if (!d.text) return
          const block = document.createElement('blockquote')
          block.className = 'wl-bible-inline-passage'
          const p = document.createElement('p')
          p.textContent = d.text
          const footer = document.createElement('footer')
          const link = document.createElement('a')
          link.href = d.gatewayUrl
          link.target = '_blank'
          link.rel = 'noopener noreferrer'
          link.textContent = `— ${d.reference} (${d.translation} · View ESV ↗)`
          footer.appendChild(link)
          block.appendChild(p)
          block.appendChild(footer)
          node.replaceWith(block)
        })
        .catch(() => {
          // Keep original fallback link
        })
    })
  }, [pathname])

  // Delegate hover / focus events for .wl-bible-hover links
  useEffect(() => {
    const showForTarget = (target: HTMLElement) => {
      const ref = target.getAttribute('data-bible-ref')
      if (!ref) return
      clearHideTimer()

      const rect = target.getBoundingClientRect()
      const cardWidth = Math.min(380, window.innerWidth - 24)
      const centeredLeft = rect.left + rect.width / 2 - cardWidth / 2
      const clampedLeft = Math.max(12, Math.min(centeredLeft, window.innerWidth - cardWidth - 12))
      const showBelow = rect.top < 220

      setCoords({
        top: showBelow ? rect.bottom + 10 : rect.top - 10,
        left: clampedLeft,
        placement: showBelow ? 'below' : 'above',
      })
      setActiveRef(ref)

      const cached = clientCache.get(ref.trim().toLowerCase())
      if (cached) {
        setPassage(cached)
        setLoading(false)
      } else {
        setLoading(true)
        setPassage(null)
        loadPassage(ref)
          .then((d) => {
            setPassage(d)
            setLoading(false)
          })
          .catch(() => {
            setLoading(false)
          })
      }
    }

    const onMouseOver = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest?.(
        '.wl-bible-hover[data-bible-ref]',
      ) as HTMLElement | null
      if (el) {
        showForTarget(el)
      }
    }

    const onMouseOut = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest?.(
        '.wl-bible-hover[data-bible-ref]',
      ) as HTMLElement | null
      if (el) {
        scheduleHide()
      }
    }

    const onFocusIn = (e: FocusEvent) => {
      const el = (e.target as HTMLElement | null)?.closest?.(
        '.wl-bible-hover[data-bible-ref]',
      ) as HTMLElement | null
      if (el) {
        showForTarget(el)
      }
    }

    const onFocusOut = (e: FocusEvent) => {
      const el = (e.target as HTMLElement | null)?.closest?.(
        '.wl-bible-hover[data-bible-ref]',
      ) as HTMLElement | null
      if (el) {
        scheduleHide()
      }
    }

    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest?.(
        '.wl-bible-hover[data-bible-ref]',
      ) as HTMLElement | null
      if (el) {
        // On touch devices or first click, open the Scripture popover inline
        e.preventDefault()
        showForTarget(el)
        return
      }
      const inPopover = (e.target as HTMLElement | null)?.closest?.('.wl-bible-popover')
      if (!inPopover) {
        setActiveRef(null)
      }
    }

    document.addEventListener('mouseover', onMouseOver)
    document.addEventListener('mouseout', onMouseOut)
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('focusout', onFocusOut)
    document.addEventListener('click', onClick)

    return () => {
      document.removeEventListener('mouseover', onMouseOver)
      document.removeEventListener('mouseout', onMouseOut)
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('focusout', onFocusOut)
      document.removeEventListener('click', onClick)
    }
  }, [])

  if (!activeRef) return null

  return (
    <div
      className="wl-bible-popover"
      role="tooltip"
      onMouseEnter={clearHideTimer}
      onMouseLeave={scheduleHide}
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        transform: coords.placement === 'above' ? 'translateY(-100%)' : 'none',
        zIndex: 9999,
      }}
    >
      <div className="wl-bible-popover-header">
        <span className="wl-bible-popover-title">
          <BookOpen size={14} style={{ color: 'var(--purple-bright)', flexShrink: 0 }} />
          <span>{passage?.reference || activeRef}</span>
        </span>
        <span className="wl-bible-popover-badge">{passage?.translation || 'Scripture'}</span>
      </div>

      <div className="wl-bible-popover-body">
        {loading ? (
          <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
            Loading Scripture passage…
          </span>
        ) : passage?.text ? (
          passage.text
        ) : (
          <span>Click below to read the full passage in the English Standard Version (ESV).</span>
        )}
      </div>

      <div className="wl-bible-popover-footer">
        <a
          href={
            passage?.gatewayUrl ||
            `https://www.biblegateway.com/passage/?search=${encodeURIComponent(activeRef)}&version=ESV`
          }
          target="_blank"
          rel="noopener noreferrer"
          className="wl-bible-popover-link"
        >
          <span>Read Full Passage on BibleGateway (ESV)</span>
          <ExternalLink size={12} />
        </a>
      </div>
    </div>
  )
}
